import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useMemo,
  useState,
} from 'react'
import {
  KibSuperSelect,
  type KibSuperSelectOption,
} from '@chewy/kib-fields-new-react'
import type { Field } from '@pbt/pbt-ui-components'

import { generateQuantitySuggestions } from '../../inventoryUtils'

import styles from './QuantityAutocompleteField.module.scss'

const MAX_SUGGESTIONS = 3

interface QuantityAutocompleteFieldProps {
  autoFocus?: boolean
  disabled?: boolean
  field: Field
  invalid?: boolean
  label?: string
  messagesContent?: ReactNode
  orderedQuantity: number
  perPackageAmount: number
  required?: boolean
}

export const QuantityAutocompleteField = ({
  field,
  orderedQuantity,
  perPackageAmount,
  disabled = false,
  required = false,
  label,
  invalid = false,
  messagesContent,
  autoFocus = false,
}: QuantityAutocompleteFieldProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const fieldValue = field.value ? field.value.toString() : undefined

  const allOptions: KibSuperSelectOption[] = useMemo(() => {
    const suggestions = generateQuantitySuggestions(
      orderedQuantity,
      perPackageAmount,
    )
      .slice(0, MAX_SUGGESTIONS)
      .map((n: number): KibSuperSelectOption => {
        const str = n.toString()
        return { label: str, value: str }
      })

    if (fieldValue && !suggestions.some((opt) => opt.value === fieldValue)) {
      return [{ label: fieldValue, value: fieldValue }, ...suggestions]
    }

    return suggestions
  }, [orderedQuantity, perPackageAmount, fieldValue])

  const selectedOption: KibSuperSelectOption[] = useMemo(
    () => (fieldValue ? [{ label: fieldValue, value: fieldValue }] : []),
    [fieldValue],
  )

  const handleChange = (newOptions: KibSuperSelectOption[]) => {
    const numericValue = Number(newOptions[0]?.value)
    field.setValue(Number.isNaN(numericValue) ? null : numericValue)
  }

  const onQuery = (
    v?: string,
    e?:
      | ChangeEvent<HTMLInputElement>
      | ReactMouseEvent<HTMLElement>
      | FocusEvent<HTMLInputElement>
      | KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e?.type === 'change' && v !== undefined) {
      const cleanValue = v.replace(/\D/g, '')
      field.setValue(cleanValue === '' ? null : Number(cleanValue))
    }
  }

  return (
    <div className={styles.quantityFieldScope}>
      <KibSuperSelect
        autoFocus={autoFocus}
        disabled={disabled}
        invalid={invalid}
        label={label || ''}
        messagesContent={messagesContent}
        options={isOpen ? allOptions : []}
        placeholder=""
        required={required}
        value={selectedOption}
        onBlur={() => setIsOpen(false)}
        onChange={handleChange}
        onFocus={() => setIsOpen(true)}
        onQuery={onQuery}
      />
    </div>
  )
}
